import { Locator, Page } from '@playwright/test';
import { decodePng, cropPixels } from './png-codec.js';
import * as fs from 'fs';
import { promisify } from 'util';
import type { Rect, PixelData } from './types.js';

const readFile = promisify(fs.readFile);

// --- TYPE DEFINITIONS ---

// Re-export the standardized type
export type { PixelData } from './types.js';

/**
 * @deprecated Use PixelData from './types.js' instead
 * Legacy interface kept for backwards compatibility only
 */
export interface PngData {
    width: number;
    height: number;
    data: Uint8Array;
}

/**
 * Interface for the coordinates of a found match.
 */
export interface MatchCoordinate {
    x: number;
    y: number;
}

/**
 * Configuration options for the image matching function.
 */
export interface ImageMatchOptions {
    /** Allowed per-channel pixel difference (0-255). Lower = stricter match. Default: 20. */
    threshold?: number;
    /** How to handle transparency (Alpha channel). */
    alphaMode?: 'mask' | 'strict';
    /** Optional crop rectangle (relative to the element/page) to narrow the search area. */
    cropRect?: Rect;
}

// --- CORE PIXEL COMPARISON LOGIC ---

/**
 * Compares the RGBA pixel at byte offset `s` of the screen data with the one at `r` of the reference data. It runs once
 * per compared pixel of every candidate position, so it reads the bytes in place: a `subarray` view per pixel (as this
 * once took) allocated two objects per comparison and made the search ~12x slower.
 * @param threshold Allowed per-channel difference (0-255).
 * @param alphaMode 'mask': a mostly transparent reference pixel (alpha < 10) matches any screen pixel, any other must
 *   match on RGB. 'strict': RGB and alpha must all match.
 * @returns True if the pixels are considered similar enough.
 */
function pixelsSimilarAt(screen: Uint8Array, s: number, ref: Uint8Array, r: number, threshold: number, alphaMode: 'mask' | 'strict'): boolean {
    if (alphaMode === 'mask' && ref[r + 3] < 10) {
        return true;
    }
    return Math.abs(screen[s] - ref[r]) <= threshold
        && Math.abs(screen[s + 1] - ref[r + 1]) <= threshold
        && Math.abs(screen[s + 2] - ref[r + 2]) <= threshold
        && (alphaMode === 'mask' || Math.abs(screen[s + 3] - ref[r + 3]) <= threshold);
}

/**
 * Checks if a reference image region matches a sub-rectangle of the screen data at (x, y).
 */
function regionMatches(
    screenData: Uint8Array, 
    screenWidth: number, 
    refData: Uint8Array, 
    refWidth: number, 
    refHeight: number, 
    x: number, 
    y: number, 
    threshold: number, 
    alphaMode: 'mask' | 'strict' = 'mask'
): boolean {
    // Boundary check: screenData.length / 4 / screenWidth gives actual height.
    if (x + refWidth > screenWidth || y + refHeight > screenData.length / 4 / screenWidth) {
        return false;
    }

    for (let j = 0; j < refHeight; j++) {
        for (let i = 0; i < refWidth; i++) {
            // RGBA data is 4 bytes per pixel.
            const sIdx = ((y + j) * screenWidth + (x + i)) * 4;
            const rIdx = (j * refWidth + i) * 4;

            if (!pixelsSimilarAt(screenData, sIdx, refData, rIdx, threshold, alphaMode)) {
                return false;
            }
        }
    }
    return true;
}

/**
 * Finds all non-overlapping top-left coordinates where the reference image matches.
 */
function findAllMatches(
  screenData: Uint8Array, 
  screenWidth: number, 
  screenHeight: number, 
  refData: Uint8Array, 
  refWidth: number, 
  refHeight: number, 
  threshold: number, 
  alphaMode: 'mask' | 'strict' = 'mask'
): MatchCoordinate[] {
    const matches: MatchCoordinate[] = [];
    
    // Only search if template fits inside the screen
    if (refWidth > screenWidth || refHeight > screenHeight) {
         return matches; 
    }

    for (let y = 0; y <= screenHeight - refHeight; y++) {
        for (let x = 0; x <= screenWidth - refWidth; x++) {
            
            // Optimization for non-overlapping matches: skip the area already covered by the last match
            if (matches.length > 0) {
                const lastMatch = matches[matches.length - 1];
                // Check if the current search window (x, y) is inside the last found bounding box on the same row.
                if (y === lastMatch.y && x < lastMatch.x + refWidth) {
                    x = lastMatch.x + refWidth - 1; // Jump x to the start of the next possible column
                    continue;
                }
            }

            if (regionMatches(screenData, screenWidth, refData, refWidth, refHeight, x, y, threshold, alphaMode)) {
                matches.push({ x, y });
                // Jump search cursor past the detected region
                x += refWidth - 1; // -1 because the outer loop increments x
            }
        }
    }
    return matches;
}

// --- HIGH-LEVEL INTEGRATION & UTILITY FUNCTIONS ---

/**
 * Takes a screenshot of a Playwright source (Page or Locator) and optionally crops it.
 * @param source The Playwright object to screenshot.
 * @param cropRect The area to crop, relative to the source.
 * @returns The PngData object for the resulting image.
 */
async function getScreenshotPngData(source: Page | Locator, cropRect?: ImageMatchOptions['cropRect']): Promise<PixelData> {
    const buffer = await source.screenshot();
    let pixels = decodePng(buffer);

    if (cropRect) {
        const { x, y, width, height } = cropRect;
        
        // Ensure crop dimensions are valid
        if (x < 0 || y < 0 || x + width > pixels.width || y + height > pixels.height) {
            throw new Error(`Crop rectangle [x:${x}, y:${y}, w:${width}, h:${height}] is out of bounds of the screenshot [w:${pixels.width}, h:${pixels.height}].`);
        }

        pixels = cropPixels(pixels, x, y, width, height);
    }

    return pixels;
}

/**
 * Finds all occurrences of the reference image within a Playwright element's screenshot.
 * @param source The Playwright Page or Locator to take a screenshot of.
 * @param referenceImageSource The path to the PNG image file to search for OR a png.js PNG object.
 * @param options Configuration options.
 * @returns List of coordinates relative to the element's top-left corner.
 */
export async function findImageMatches(
    source: Page | Locator, 
    referenceImageSource: string | PixelData,
    options: ImageMatchOptions = {}
): Promise<MatchCoordinate[]> {
    const threshold = options.threshold ?? 20; 
    const alphaMode = options.alphaMode ?? 'mask';
    const cropRect = options.cropRect;

    // 1. Load and process reference image
    let refData: PixelData;

    if (typeof referenceImageSource === 'string') {
        // Source is a file path
        let refBuffer = await readFile(referenceImageSource);
        
        // Fix potential PNG corruption by removing trailing bytes after IEND
        const iendMarker = Buffer.from([0x49, 0x45, 0x4E, 0x44, 0xae, 0x42, 0x60, 0x82]);
        const iendIndex = refBuffer.lastIndexOf(iendMarker);
        
        if (iendIndex !== -1) {
            // Truncate buffer after IEND chunk
            refBuffer = refBuffer.subarray(0, iendIndex + iendMarker.length);
        }
        
        refData = decodePng(refBuffer);
    } else {
        // Source is already pixel data
        refData = referenceImageSource; 
    }

    // 2. Capture and process screen/locator image
    const screenData = await getScreenshotPngData(source, cropRect); // Assuming getScreenshotPngData is defined

    // 3. Run the core matching algorithm
    return findAllMatches( // Assuming findAllMatches is defined
        screenData.data, 
        screenData.width, 
        screenData.height, 
        refData.data, 
        refData.width, 
        refData.height, 
        threshold,
        alphaMode
    );
}
/**
 * Waits until the reference image appears on screen.
 */
export async function waitForImageOnScreen(
    source: Page | Locator,
    referenceImagePath: string,
    options: ImageMatchOptions & { maxWait?: number; stepDelay?: number } = {}
): Promise<boolean> {
    const maxWait = options.maxWait ?? 5000;
    const stepDelay = options.stepDelay ?? 500;
    const start = Date.now();

    while (Date.now() - start < maxWait) {
        const matches = await findImageMatches(source, referenceImagePath, options);
        if (matches.length > 0) {
            return true;
        }
        await new Promise(r => setTimeout(r, stepDelay));
    }

    return false;
}

/**
 * Clicks the center of the first area matching the reference image.
 * This should only be used on a Playwright Page object.
 */
export async function clickImageOnScreen(
    page: Page,
    referenceImagePath: string,
    options: ImageMatchOptions = {}
): Promise<void> {
    const matches = await findImageMatches(page, referenceImagePath, options);
    
    if (matches.length === 0) {
        throw new Error(`Image not found on screen using path: ${referenceImagePath}.`);
    }

    // Load reference image dimensions again (needed for calculating the center)
    let refBuffer = await readFile(referenceImagePath);
    
    // Fix potential PNG corruption by removing trailing bytes after IEND
    const iendMarker = Buffer.from([0x49, 0x45, 0x4E, 0x44, 0xae, 0x42, 0x60, 0x82]);
    const iendIndex = refBuffer.lastIndexOf(iendMarker);
    
    if (iendIndex !== -1) {
        // Truncate buffer after IEND chunk
        refBuffer = refBuffer.subarray(0, iendIndex + iendMarker.length);
    }
    
    const refPixels = decodePng(refBuffer);
    const refWidth = refPixels.width;
    const refHeight = refPixels.height;

    // Determine the click coordinate based on the first match
    const { x, y } = matches[0];
    const clickX = x + refWidth / 2;
    const clickY = y + refHeight / 2;
    
    // Adjust coordinates if a cropRect was used (since matches are relative to the cropped area)
    const cropXOffset = options.cropRect?.x ?? 0;
    const cropYOffset = options.cropRect?.y ?? 0;

    await page.mouse.click(clickX + cropXOffset, clickY + cropYOffset);
}

/**
 * Counts how many instances of the reference image appear on screen.
 */
export async function getImageCountOnScreen(
    source: Page | Locator,
    referenceImagePath: string,
    options: ImageMatchOptions = {}
): Promise<number> {
    const matches = await findImageMatches(source, referenceImagePath, options);
    return matches.length;
}
